import {
  ComponentPropsWithoutRef,
  forwardRef,
  PropsWithChildren,
  ReactElement,
} from "react";
import * as styles from "./Accordion.css";
import { Accordion as IAccordion, Link as ILink } from "~/sanity/types";
import * as RadixAccordion from "@radix-ui/react-accordion";
import PortableText, { IPortableText } from "../portableText/PortableText";
// @ts-ignore
import Chevron from "@material-design-icons/svg/filled/keyboard_arrow_down.svg?react";
import { Paragraph } from "./Paragraph";
import { ButtonLink } from "./ButtonLink";
import { Socials } from "./Socials";
import Link from "./Link";
import { IImage, Image } from "./Image";
// @ts-ignore
import ArrowOutward from "@material-design-icons/svg/filled/arrow_outward.svg?react";

export type AccordionProps = ComponentPropsWithoutRef<"div"> &
  PropsWithChildren & {
    accordionItems: IAccordion;
    defaultOpen?: boolean;
    onValueChange?: (value: string) => void;
  };

export const Accordion = ({
  accordionItems,
  defaultOpen = true,
  onValueChange,
  ...props
}: AccordionProps) => {
  return (
    <RadixAccordion.Root
      className={styles.accordionWrapper}
      type="single"
      defaultValue={defaultOpen ? "item-0" : undefined}
      collapsible
      onValueChange={onValueChange}
    >
      {accordionItems.map((item, index) => (
        <RadixAccordion.Item
          className={styles.accordionItem}
          value={`item-${index}`}
          key={item._key ?? `accordion-item-${index}`}
        >
          {item.question && (
            <AccordionItem
              key="question"
              title={item.question.title}
              text={item.question.text as IPortableText}
              links={item.question.links}
            />
          )}
          {item.item && (
            <AccordionItem
              key="item"
              title={item.item.title}
              text={item.item.text as IPortableText}
              links={item.item.links}
              list={item.item.list}
              cardArray={
                (
                  item.item as {
                    cardArray?: {
                      image?: IImage;
                      title?: string;
                      text?: string;
                      link?: string;
                    }[];
                  }
                ).cardArray
              }
              socials={item.item.socials}
            />
          )}
        </RadixAccordion.Item>
      ))}
    </RadixAccordion.Root>
  );
};

const AccordionItem = ({
  title,
  text,
  links,
  list,
  cardArray,
  socials,
}: {
  title?: string;
  text?: IPortableText;
  links?: ILink[];
  list?: { text: string; icon: ReactElement; link?: string }[];
  cardArray?: {
    image?: IImage;
    title?: string;
    text?: string;
    link?: string;
  }[];
  socials?: IAccordion["socials"];
}) => {
  return (
    <>
      <AccordionTrigger>
        <Paragraph size="uiInputsAccordion" className={styles.heading}>
          {title}
        </Paragraph>
      </AccordionTrigger>
      <AccordionContent>
        {text && <PortableText blocks={text as IPortableText} />}
        {links && (
          <div className={styles.links}>
            {links.map((link, index) => (
              <ButtonLink theme="outlineDark" key={index} link={link}>
                {link.text}
              </ButtonLink>
            ))}
          </div>
        )}
        {list && (
          <div className={styles.list}>
            {list.map((listItem, index) => {
              return (
                <div key={index} className={styles.listItem}>
                  <div className={styles.listIcon}>{listItem.icon}</div>
                  {listItem.link ? (
                    <Link
                      _type={"link"}
                      to={listItem.link}
                      className={styles.listItemLink}
                    >
                      <Paragraph>{listItem.text}</Paragraph>
                    </Link>
                  ) : (
                    <Paragraph>{listItem.text}</Paragraph>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {cardArray && (
          <div className={styles.cardArray}>
            {cardArray.map((cardItem, index) => (
              <div
                key={`card-${index}`}
                style={{ display: "contents" }}
              >
                <div className={styles.cardArrayItem}>
                  {cardItem.image && (
                    <div className={styles.cardArrayImageWrapper}>
                      <Image
                        image={cardItem.image}
                        width={180}
                        className={styles.cardArrayImage}
                      />
                    </div>
                  )}
                  <div className={styles.cardArrayContent}>
                    {cardItem.title && (
                      <Paragraph
                        bold
                        size="xl"
                        className={styles.cardArrayTitle}
                      >
                        {cardItem.title}
                      </Paragraph>
                    )}
                    {cardItem.text && (
                      <Paragraph size="sm" className={styles.cardArrayText}>
                        {cardItem.text}
                      </Paragraph>
                    )}
                    {cardItem.link && (
                      <div className={styles.cardArrayItemLink}>
                        <ButtonLink
                          isPrimary={false}
                          theme="colour"
                          link={{
                            _type: "link",
                            type: "external",
                            slug: cardItem.link,
                            text: "View Partner",
                            newTab: true,
                          }}
                          icons={{ iconPrimary: <ArrowOutward /> }}
                        >
                          View Partner
                        </ButtonLink>
                      </div>
                    )}
                  </div>
                </div>
                {index < cardArray.length - 1 && (
                  <div className={styles.cardArraySeparator} />
                )}
              </div>
            ))}
          </div>
        )}
        {socials && (
          <Socials theme="dark" isPrimary={true} size="sm" {...socials} />
        )}
      </AccordionContent>
    </>
  );
};
const AccordionTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button">
>(({ children, ...props }, ref) => {
  return (
    <RadixAccordion.Header>
      <RadixAccordion.Trigger
        {...props}
        ref={ref}
        className={styles.accordionTrigger}
      >
        <div className={styles.triggerChildren}>
          {children}
          <div className={styles.iconContainer}>
            <Chevron />
          </div>
        </div>
      </RadixAccordion.Trigger>
    </RadixAccordion.Header>
  );
});

const AccordionContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div">
>(({ children, ...props }, ref) => {
  return (
    <RadixAccordion.Content
      {...props}
      ref={ref}
      className={styles.accordionContent}
    >
      {children}
    </RadixAccordion.Content>
  );
});
